import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = useMemo(() => params.get("token") || "", [params]);

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (password.length < 6) {
            setErr("Password must be at least 6 characters.");
            return;
        }
        if (password !== confirm) {
            setErr("Passwords do not match.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            toast.success("Password updated. Please sign in.");
            navigate("/login", { replace: true });
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    };

    if (!token) {
        return (
            <AuthShell
                title="Missing reset link"
                subtitle="This page needs a valid token to change your password."
                side="virtual"
            >
                <div
                    data-testid="reset-missing-token"
                    className="rounded-2xl bg-white border border-red-200 shadow-soft p-6 space-y-3"
                >
                    <div className="flex items-center gap-3 text-red-600">
                        <AlertCircle />
                        <div className="font-display text-2xl">Link is incomplete</div>
                    </div>
                    <p className="text-navy-500 text-sm leading-relaxed">
                        We couldn&apos;t find a reset token in the URL. Please request a fresh
                        link from the forgot-password page.
                    </p>
                    <div className="pt-2 flex gap-4">
                        <Link
                            to="/forgot-password"
                            data-testid="reset-request-new"
                            className="text-sm text-navy-700 font-semibold hover:text-gold-600"
                        >
                            Request a new link
                        </Link>
                        <Link
                            to="/login"
                            data-testid="reset-back-login"
                            className="text-sm text-navy-500 hover:text-navy-700"
                        >
                            Back to sign in
                        </Link>
                    </div>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell
            title="Set a new password"
            subtitle="Pick something you'll remember. Minimum 6 characters."
            side="virtual"
        >
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
                <div>
                    <Label
                        htmlFor="rp-password"
                        className="text-navy-700 text-xs uppercase tracking-widest"
                    >
                        New password
                    </Label>
                    <Input
                        id="rp-password"
                        type="password"
                        required
                        minLength={6}
                        data-testid="reset-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                    />
                </div>
                <div>
                    <Label
                        htmlFor="rp-confirm"
                        className="text-navy-700 text-xs uppercase tracking-widest"
                    >
                        Confirm new password
                    </Label>
                    <Input
                        id="rp-confirm"
                        type="password"
                        required
                        minLength={6}
                        data-testid="reset-confirm"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                    />
                </div>

                {err && (
                    <div
                        data-testid="reset-error"
                        className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                    >
                        {err}
                    </div>
                )}

                <Button
                    type="submit"
                    disabled={busy}
                    data-testid="reset-submit"
                    className="w-full h-12 rounded-full bg-navy-700 hover:bg-navy-800 text-white font-semibold"
                >
                    {busy ? "Updating password…" : "Update password"}
                </Button>

                <div className="text-sm text-navy-500 text-center">
                    <Link
                        to="/login"
                        data-testid="reset-login-link"
                        className="text-navy-700 font-semibold hover:text-gold-600"
                    >
                        Back to sign in
                    </Link>
                </div>
            </form>
        </AuthShell>
    );
}
